import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CredentialCard = ({ 
  credential, 
  isSelected, 
  onSelect, 
  onView, 
  onShare, 
  className = '' 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success';
      case 'expired': return 'text-error';
      case 'pending': return 'text-warning';
      case 'revoked': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'ShieldCheck';
      case 'expired': return 'ShieldX';
      case 'pending': return 'Clock';
      case 'revoked': return 'ShieldAlert';
      default: return 'Shield';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isExpiringSoon = () => {
    if (!credential?.expirationDate) return false;
    const expirationDate = new Date(credential.expirationDate);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow?.setDate(thirtyDaysFromNow?.getDate() + 30);
    return expirationDate <= thirtyDaysFromNow && expirationDate > new Date();
  };

  return (
    <div className={`credential-card relative group cursor-pointer transition-all duration-200 hover:shadow-lg ${
      isSelected ? 'ring-2 ring-primary' : ''
    } ${className}`}>
      {/* Selection Checkbox */}
      <div className="absolute top-3 left-3 z-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(credential?.id, e?.target?.checked)}
          className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
        />
      </div>
      {/* Quick Actions */}
      <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="flex space-x-1">
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 bg-background/80 backdrop-blur-sm"
            onClick={(e) => {
              e?.stopPropagation();
              onShare(credential);
            }}
          >
            <Icon name="Share" size={14} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 bg-background/80 backdrop-blur-sm"
            onClick={(e) => {
              e?.stopPropagation();
              onView(credential);
            }}
          >
            <Icon name="Eye" size={14} />
          </Button>
        </div>
      </div>
      {/* Expiring Soon Badge */}
      {isExpiringSoon() && (
        <div className="absolute top-3 left-1/2 transform -translate-x-1/2 z-10">
          <div className="bg-warning text-warning-foreground px-2 py-1 rounded-full text-xs font-medium">
            Expiring Soon
          </div>
        </div>
      )}
      <div className="p-6" onClick={() => onView(credential)}>
        {/* Issuer Logo and Info */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={credential?.issuer?.logo}
                alt={credential?.issuer?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-foreground truncate">
                {credential?.title}
              </h3>
              <p className="text-sm text-muted-foreground truncate">
                {credential?.issuer?.name}
              </p>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className={`flex items-center space-x-1 ${getStatusColor(credential?.status)}`}>
            <Icon name={getStatusIcon(credential?.status)} size={16} />
            <span className="text-xs font-medium capitalize">{credential?.status}</span>
          </div>
        </div>

        {/* Credential Type */}
        <div className="mb-4">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
            {credential?.type}
          </span>
        </div>

        {/* Dates */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Issued:</span>
            <span className="text-foreground">{formatDate(credential?.issuedDate)}</span>
          </div>
          {credential?.expirationDate && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Expires:</span>
              <span className={`${
                credential?.status === 'expired' ? 'text-error' : isExpiringSoon() ?'text-warning' : 'text-foreground'
              }`}>
                {formatDate(credential?.expirationDate)}
              </span>
            </div>
          )}
        </div>

        {/* Verification Info */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Link" size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Blockchain Anchored
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Key" size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {credential?.cryptographicProof ? 'Signed' : 'Unsigned'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialCard;